import { getAuthorName, type ArticleData } from '@benzinga/article-manager';
import type { MetaProps } from '@benzinga/seo';

import type { PageConfig } from '../../_components/page/pageConfig';
import { deriveQuoteActiveTabFromPathname } from '../../../src/utils/quote/active-tab';
import { buildClientRouteMeta } from './buildClientRouteMeta';
import { getChartbeatSectionFromQuoteTitle } from './quoteChartbeat';
import type { RouteMetaPayload } from './routeMeta';

const compactStrings = (values: (string | null | undefined)[] = []): string[] =>
  values.filter((value): value is string => Boolean(value));

export function shouldDisablePageTracking(
  routeMeta: RouteMetaPayload | null,
  pageConfig: PageConfig = {},
): boolean {
  if (routeMeta?.disablePageTracking) return true;
  if (pageConfig.error === 404 || pageConfig.error === 410) return true;
  if (pageConfig.statusCode === 404 || pageConfig.statusCode === 410) return true;
  return false;
}

export type ChartbeatPageview = {
  authorName: string;
  path: string;
  sections: string;
  title: string;
};

function isStoryPath(pathname: string): boolean {
  return pathname.startsWith('/article/') || /\/\d{2}\/\d{2}\//.test(pathname);
}

function isQuoteTickerPath(pathname: string): boolean {
  return /^\/quote\/[^/]+/.test(pathname);
}

export function resolveChartbeatPageview(
  routeMeta: RouteMetaPayload | null,
  pageConfig: PageConfig = {},
  pathname: string,
): ChartbeatPageview | null {
  if (shouldDisablePageTracking(routeMeta, pageConfig)) return null;
  if (!routeMeta && isStoryPath(pathname)) return null;

  const metaProps = routeMeta?.metaProps ?? pageConfig.metaProps;

  if (isQuoteTickerPath(pathname)) {
    const quoteTitlesByTab = routeMeta?.quoteTitlesByTab;
    const activeTab = deriveQuoteActiveTabFromPathname(pathname);
    const quotePageTitle = quoteTitlesByTab?.[activeTab] || quoteTitlesByTab?.overview || '';
    if (!quotePageTitle) return null;

    return {
      authorName: '',
      path: pathname,
      sections: getChartbeatSectionFromQuoteTitle(quotePageTitle),
      title: quotePageTitle,
    };
  }

  const article = routeMeta?.article as ArticleData | undefined;
  const articleAuthorName = article ? getAuthorName(article) : '';
  const articleSections = compactStrings(
    [...(article?.channels ?? []), ...(article?.tags ?? [])].map(category => category.name),
  );
  const metaSections = metaProps?.dimensions?.channels?.length
    ? metaProps.dimensions.channels
    : (metaProps?.vertical ?? []);

  return {
    authorName: articleAuthorName || metaProps?.dimensions?.authorName || metaProps?.author || 'Benzinga',
    path: pathname,
    sections: (articleSections.length ? articleSections : metaSections).join(', '),
    title: metaProps?.title ?? '',
  };
}

function resolveSegmentMeta(
  routeMeta: RouteMetaPayload | null,
  scrollMeta: MetaProps | null,
  pathname: string,
  pageConfig: PageConfig,
): MetaProps {
  if (scrollMeta) return scrollMeta;
  if (routeMeta?.metaProps) return routeMeta.metaProps;
  if (pageConfig.metaProps) return pageConfig.metaProps;
  return buildClientRouteMeta(pathname, pageConfig.pageTargeting);
}

export function mergeRoutePayload(
  routeMeta: RouteMetaPayload | null,
  pageConfig: PageConfig = {},
): RouteMetaPayload {
  return {
    ...routeMeta,
    pageTargeting: routeMeta?.pageTargeting ?? pageConfig.pageTargeting,
    disableFooterDisplayAd:
      routeMeta?.disableFooterDisplayAd ??
      routeMeta?.disableStickyFooter ??
      routeMeta?.disableRaptiveFooter ??
      pageConfig.disableFooterDisplayAd ??
      pageConfig.disableStickyFooter ??
      pageConfig.disableRaptiveFooter ??
      false,
  };
}

export interface ResolvedPageAnalytics {
  shouldTrack: boolean;
  meta: MetaProps;
  chartbeat: ChartbeatPageview | null;
  payload: RouteMetaPayload;
}

export function resolvePageAnalytics({
  routeMeta,
  pageConfig = {},
  pathname,
  scrollMeta = null,
}: {
  routeMeta: RouteMetaPayload | null;
  pageConfig?: PageConfig;
  pathname: string;
  scrollMeta?: MetaProps | null;
}): ResolvedPageAnalytics {
  const shouldTrack = !shouldDisablePageTracking(routeMeta, pageConfig);
  const payload = mergeRoutePayload(routeMeta, pageConfig);
  const meta = resolveSegmentMeta(routeMeta, scrollMeta, pathname, pageConfig);
  const chartbeat = resolveChartbeatPageview(routeMeta, pageConfig, pathname);

  return { shouldTrack, meta, chartbeat, payload };
}
